import { useCallback } from 'react'
import type { Task } from '@/types/task'
import type { FamilyMember } from '@/types/family'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Routine } from '@/types/actionable'
import type { RescheduleResult } from '@/hooks/useActionableInstances'

interface UseScheduleActionsDeps {
  tasks: Task[]
  events: CalendarEvent[]
  allRoutines: Routine[]
  familyMembers: FamilyMember[]
  viewedDate: Date
  updateTask: (id: string, updates: Partial<Task>) => void
  updateRoutine: (id: string, updates: Partial<Routine>) => void
  deleteRoutine: (id: string) => Promise<unknown>
  updateEventAssignment: (eventId: string, memberId: string | null) => void
  updateEventAssignmentAll: (eventId: string, memberIds: string[]) => void
  markDone: (entityType: 'routine' | 'calendar_event', entityId: string, date: Date, completedAt?: Date) => Promise<boolean>
  undoDone: (entityType: 'routine' | 'calendar_event', entityId: string, date: Date) => Promise<boolean>
  skip: (entityType: 'routine' | 'calendar_event', entityId: string, date: Date) => Promise<boolean>
  reschedule: (entityType: 'routine' | 'calendar_event', entityId: string, fromDate: Date, toDate: Date, opts?: { dayOnly?: boolean }) => Promise<RescheduleResult | null>
  undoReschedule: (previous: RescheduleResult) => Promise<boolean>
  refreshDateInstances: () => void
  pushAction: (message: string, undoFn: () => void) => void
}

export function useScheduleActions({
  tasks,
  events,
  allRoutines,
  familyMembers,
  viewedDate,
  updateTask,
  updateRoutine,
  deleteRoutine,
  updateEventAssignment,
  updateEventAssignmentAll,
  markDone,
  undoDone,
  skip,
  reschedule,
  undoReschedule,
  refreshDateInstances,
  pushAction,
}: UseScheduleActionsDeps) {

  const onAssignTask = useCallback((taskId: string, memberId: string | null) => {
    const task = tasks.find(t => t.id === taskId)
    const prevAssignedTo = task?.assignedTo
    const taskTitle = task?.title || 'Task'
    updateTask(taskId, { assignedTo: memberId ?? undefined })

    const memberName = memberId ? familyMembers.find(m => m.id === memberId)?.name : null
    const message = memberName ? `Assigned "${taskTitle}" to ${memberName}` : `Unassigned "${taskTitle}"`
    pushAction(message, () => {
      updateTask(taskId, { assignedTo: prevAssignedTo ?? undefined })
    })
  }, [tasks, familyMembers, updateTask, pushAction])

  const onAssignTaskAll = useCallback((taskId: string, memberIds: string[]) => {
    const task = tasks.find(t => t.id === taskId)
    const prevAssignedToAll = task?.assignedToAll || []
    const prevAssignedTo = task?.assignedTo
    const taskTitle = task?.title || 'Task'
    updateTask(taskId, { assignedToAll: memberIds, assignedTo: memberIds[0] ?? undefined })

    const memberNames = memberIds.map(id => familyMembers.find(m => m.id === id)?.name).filter(Boolean)
    const message = memberIds.length > 0
      ? `Assigned "${taskTitle}" to ${memberNames.join(', ')}`
      : `Unassigned "${taskTitle}"`
    pushAction(message, () => {
      updateTask(taskId, { assignedToAll: prevAssignedToAll, assignedTo: prevAssignedTo ?? undefined })
    })
  }, [tasks, familyMembers, updateTask, pushAction])

  const onAssignEvent = useCallback((eventId: string, memberId: string | null) => {
    updateEventAssignment(eventId, memberId)
  }, [updateEventAssignment])

  const onAssignEventAll = useCallback((eventId: string, memberIds: string[]) => {
    updateEventAssignmentAll(eventId, memberIds)
  }, [updateEventAssignmentAll])

  const onAssignRoutine = useCallback((routineId: string, memberId: string | null) => {
    updateRoutine(routineId, { assigned_to: memberId })
  }, [updateRoutine])

  const onAssignRoutineAll = useCallback((routineId: string, memberIds: string[]) => {
    updateRoutine(routineId, { assigned_to_all: memberIds, assigned_to: memberIds[0] ?? null })
  }, [updateRoutine])

  const onCompleteRoutine = useCallback(async (routineId: string, completed: boolean, completedAt?: Date) => {
    const bareId = routineId.split('#')[0]
    const routine = allRoutines.find(r => r.id === bareId)
    const routineName = routine?.name || 'Routine'

    // ONE confirmation, registered only once the write has landed. Announcing
    // before the await claims a completion the database may refuse, and a
    // second wrapper announcing it too left two Undos for one gesture (Codex
    // review, 2026-09-24). Reopening is confirmed the same way, so the gesture
    // is reversible in both directions.
    const ok = completed
      ? await markDone('routine', routineId, viewedDate, completedAt)
      : await undoDone('routine', routineId, viewedDate)
    refreshDateInstances()
    if (!ok) return
    if (completed) {
      pushAction(`Completed "${routineName}"`, async () => {
        await undoDone('routine', routineId, viewedDate)
        refreshDateInstances()
      })
    } else {
      pushAction(`Reopened "${routineName}"`, async () => {
        await markDone('routine', routineId, viewedDate)
        refreshDateInstances()
      })
    }
  }, [allRoutines, viewedDate, markDone, undoDone, refreshDateInstances, pushAction])

  const onSkipRoutine = useCallback(async (routineId: string) => {
    const bareId = routineId.split('#')[0]
    const routine = allRoutines.find(r => r.id === bareId)
    const routineName = routine?.name || 'Routine'

    await skip('routine', routineId, viewedDate)
    pushAction(`Skipped "${routineName}"`, async () => {
      await undoDone('routine', routineId, viewedDate)
      refreshDateInstances()
    })
    refreshDateInstances()
  }, [allRoutines, viewedDate, skip, undoDone, refreshDateInstances, pushAction])

  /**
   * Pin a routine to a time on ONE day. `fromDate` is the day the routine is
   * moving OFF — the week grid knows it from the dragged block's column, and
   * only a single-day surface can fall back to `viewedDate`. Getting this wrong
   * writes the override onto whatever day happened to be in view.
   */
  const onPushRoutine = useCallback(async (routineId: string, date: Date, fromDate?: Date) => {
    const bareId = routineId.split('#')[0]
    const routine = allRoutines.find(r => r.id === bareId)
    const routineName = routine?.name || 'Routine'

    const previous = await reschedule('routine', routineId, fromDate ?? viewedDate, date)
    // No toast and no undo when the write failed — "Rescheduled" over an
    // unchanged grid is worse than no feedback at all.
    if (!previous) return
    pushAction(`Rescheduled "${routineName}"`, async () => {
      await undoReschedule(previous)
      refreshDateInstances()
    })
    refreshDateInstances()
  }, [allRoutines, viewedDate, reschedule, undoReschedule, refreshDateInstances, pushAction])

  /**
   * Move ONE occurrence to another day — the routine row's Move button
   * (Tomorrow / weekend / next week / a date). The rule is untouched. A timed
   * routine keeps its hour on the new day; an untimed one (Wash comforters)
   * lands untimed rather than at midnight. Returns whether the write landed.
   */
  const onMoveRoutineToDay = useCallback(async (routineId: string, day: Date): Promise<boolean> => {
    const bareId = routineId.split('#')[0]
    const routine = allRoutines.find(r => r.id === bareId)
    const routineName = routine?.name || 'Routine'

    const target = new Date(day)
    const [h, m] = (routine?.time_of_day ?? '').split(':').map(Number)
    const timed = Number.isFinite(h) && Number.isFinite(m)
    target.setHours(timed ? h : 0, timed ? m : 0, 0, 0)

    const previous = await reschedule('routine', routineId, viewedDate, target, { dayOnly: !timed })
    if (!previous) return false
    const label = target.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    pushAction(`Moved "${routineName}" to ${label}`, async () => {
      await undoReschedule(previous)
      refreshDateInstances()
    })
    refreshDateInstances()
    return true
  }, [allRoutines, viewedDate, reschedule, undoReschedule, refreshDateInstances, pushAction])

  const onDeleteRoutine = useCallback(async (routineId: string) => {
    await deleteRoutine(routineId)
  }, [deleteRoutine])

  const onCompleteEvent = useCallback(async (eventId: string, completed: boolean) => {
    const event = events.find(e => (e.google_event_id || e.id) === eventId)
    const eventName = event?.title || 'Event'

    // Same contract as a routine: the confirmation follows the write.
    const ok = completed
      ? await markDone('calendar_event', eventId, viewedDate)
      : await undoDone('calendar_event', eventId, viewedDate)
    refreshDateInstances()
    if (!ok) return
    if (completed) {
      pushAction(`Completed "${eventName}"`, async () => {
        await undoDone('calendar_event', eventId, viewedDate)
        refreshDateInstances()
      })
    } else {
      pushAction(`Reopened "${eventName}"`, async () => {
        await markDone('calendar_event', eventId, viewedDate)
        refreshDateInstances()
      })
    }
  }, [events, viewedDate, markDone, undoDone, refreshDateInstances, pushAction])

  const onSkipEvent = useCallback(async (eventId: string) => {
    const event = events.find(e => (e.google_event_id || e.id) === eventId)
    const eventName = event?.title || 'Event'

    await skip('calendar_event', eventId, viewedDate)
    pushAction(`Skipped "${eventName}"`, async () => {
      await undoDone('calendar_event', eventId, viewedDate)
      refreshDateInstances()
    })
    refreshDateInstances()
  }, [events, viewedDate, skip, undoDone, refreshDateInstances, pushAction])

  const onPushEvent = useCallback(async (eventId: string, date: Date, fromDate?: Date) => {
    const event = events.find(e => (e.google_event_id || e.id) === eventId)
    const eventName = event?.title || 'Event'

    const previous = await reschedule('calendar_event', eventId, fromDate ?? viewedDate, date)
    if (!previous) return
    pushAction(`Rescheduled "${eventName}"`, async () => {
      await undoReschedule(previous)
      refreshDateInstances()
    })
    refreshDateInstances()
  }, [events, viewedDate, reschedule, undoReschedule, refreshDateInstances, pushAction])

  return {
    onAssignTask,
    onAssignTaskAll,
    onAssignEvent,
    onAssignEventAll,
    onAssignRoutine,
    onAssignRoutineAll,
    onCompleteRoutine,
    onMoveRoutineToDay,
    onSkipRoutine,
    onPushRoutine,
    onDeleteRoutine,
    onCompleteEvent,
    onSkipEvent,
    onPushEvent,
  }
}
